import { HttpErrorResponse } from '@angular/common/http';

/**
 * Normalized error format that matches what ErrorsInterceptor produces.
 * This is the format that the error handling system expects after HTTP errors
 * are processed by the interceptor.
 */
export interface NormalizedError {
  code: string;
  message: string;
  status: number;
  originalError?: HttpErrorResponse;
  errType?: 'string' | 'object' | 'array';
  // Direct meta property for client errors (no nesting)
  meta?: Array<{
    error: string;
    metadata?: any;
  }>;
}

/**
 * Creates a normalized error object for client-side errors.
 * This matches the format produced by ErrorsInterceptor, ensuring consistent
 * error handling throughout the application.
 *
 * Use this for client-side validation errors, logic errors, or any error
 * that doesn't originate from an HTTP response.
 *
 * @param message Human-readable error message
 * @param code Error code (e.g., 'CLIENT_ERROR', 'VALIDATION_ERROR')
 * @param status HTTP-like status code (default: 500)
 * @param includeMetaForDisplay If true, includes meta array for pop/dialog error display
 * @returns Normalized error object compatible with error handling system
 *
 * @example
 * ```typescript
 * // Simple error (snack display)
 * return of(actionFailure(
 *   createClientError(
 *     'User data missing from response',
 *     'REGISTRATION_DATA_MISSING',
 *     400
 *   ),
 *   originalAction
 * ));
 *
 * // Error with meta for popover display
 * return of(actionFailure(
 *   createClientError(
 *     'The authenticator was previously registered',
 *     'WEBAUTHN_ERROR',
 *     400,
 *     true  // includeMetaForDisplay - adds meta array
 *   ),
 *   originalAction
 * ));
 *
 * // Error with additional metadata
 * const error = createClientError(
 *   'Validation failed',
 *   'VALIDATION_ERROR',
 *   422,
 *   true
 * );
 * error.meta[0].metadata = { fields: ['email', 'password'] };
 * ```
 */
export function createClientError(
  message: string,
  code = 'CLIENT_ERROR',
  status = 500,
  includeMetaForDisplay = false
): NormalizedError {
  const error: NormalizedError = {
    code,
    message,
    status,
    errType: 'object'
  };

  // Add meta directly for pop/dialog error display
  // No more fake nested HTTP structure!
  if (includeMetaForDisplay) {
    error.meta = [{
      error: message
    }];
  }

  return error;
}
